import HeroBackground from "@/components/landing/HeroBackground";
import HeroHeadline from "@/components/landing/HeroHeadline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldCheck, faSparkles, faClock } from "@fortawesome/pro-solid-svg-icons";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackCta } from "@/lib/analytics/cta";
import { useEffect, useRef, useState } from "react";

// Served from /public (not bundled) so index.html can <link rel="preload"> the
// LCP hero poster at a stable URL and start the fetch before the JS executes.
const heroPoster = "/hero-poster.webp";
const heroPosterMobile = "/hero-poster-mobile.webp";

// max-w-6xl = 1152px in Tailwind
const INITIAL_MAX_WIDTH_PX = 1152;
// rough horizontal breathing room at the initial size (matches px-6 on each side)
const INITIAL_HORIZONTAL_PADDING_PX = 48;
const INITIAL_BORDER_RADIUS_PX = 32;
// section is taller than the viewport; the extra height is the scroll-jack distance
const SCROLL_REGION_VH = 130;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Marketing hero with a scroll-jacked expansion: as the user scrolls through
 * the section, the video card grows from its initial inset size to fill the
 * viewport. Once fully expanded, sticky positioning releases and normal
 * scrolling resumes. A negative bottom margin pulls the next section up so
 * it sits right after the animation completes instead of after an extra
 * viewport of scroll runway.
 */
const HeroSection = () => {
  const isMobile = useIsMobile();
  const sectionRef = useRef<HTMLElement>(null);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1440,
    h: typeof window !== "undefined" ? window.innerHeight : 900,
  }));
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      const section = sectionRef.current;
      if (!section) {
        frame = 0;
        return;
      }
      const rect = section.getBoundingClientRect();
      const animationDistance = Math.max(1, rect.height - window.innerHeight);
      const scrolled = -rect.top;
      const p = Math.max(0, Math.min(1, scrolled / animationDistance));
      setProgress(p);
      frame = 0;
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const initialWidth = Math.min(
    INITIAL_MAX_WIDTH_PX,
    Math.max(0, viewport.w - INITIAL_HORIZONTAL_PADDING_PX),
  );
  // A 16:9 plate is too short on narrow screens to hold the headline, subhead
  // and CTA without clipping, so use a taller portrait plate on mobile that
  // still expands to fill the viewport on scroll.
  const initialHeight =
    viewport.w < 768 ? viewport.h * 0.7 : (initialWidth * 9) / 16;
  const cardWidth = lerp(initialWidth, viewport.w, progress);
  const cardHeight = lerp(initialHeight, viewport.h, progress);
  const cardBorderRadius = lerp(INITIAL_BORDER_RADIUS_PX, 0, progress);

  const smoothScrollTo =
    (targetId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const target = document.getElementById(targetId);
      if (!target) return;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
    };

  return (
    <section
      ref={sectionRef}
      className="bg-white"
      style={{ height: `${SCROLL_REGION_VH}vh` }}
    >
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
        <div
          style={{
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
            borderRadius: `${cardBorderRadius}px`,
          }}
          className="relative overflow-hidden bg-black"
        >
          <HeroBackground
            isMobile={isMobile}
            heroPoster={heroPoster}
            heroPosterMobile={heroPosterMobile}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
          />

          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 md:px-6">
            <div className="max-w-6xl mx-auto space-y-4 md:space-y-5 text-center">
              <HeroHeadline />

              <p className="max-w-3xl mx-auto text-lg md:text-2xl text-white font-normal">
                Earn yours with a vetted creator at every event.
              </p>

              <div className="pt-3 md:pt-4 flex flex-col gap-4 justify-center items-center">
                <button
                  type="button"
                  onClick={(event) => {
                    trackCta({ cta_id: "get_game_plan", location: "hero", destination: "#lead-form" });
                    smoothScrollTo("lead-form")(event);
                  }}
                  className="bg-cyan hover:bg-cyan/90 font-black italic uppercase tracking-wide whitespace-nowrap pl-8 pr-10 md:pl-12 md:pr-14 py-3 md:py-4 text-sm shadow-lg transition-colors rounded-full md:text-xl text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2"
                >
                  Get Your Game Plan
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    trackCta({ cta_id: "see_how_it_works", location: "hero", destination: "#what-is-fivestar" });
                    smoothScrollTo("what-is-fivestar")(event);
                  }}
                  className="text-white font-medium text-sm md:text-base underline underline-offset-4 hover:text-white/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 rounded-sm"
                >
                  See how it works
                </button>
              </div>
            </div>

            {/* Trust chips: in normal flow on mobile (below the CTAs) so they
                never overlap the buttons; pinned to the bottom of the card at
                md+ to keep the desktop layout unchanged. */}
            <div className="mt-6 px-4 flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-x-7 gap-y-2.5 text-sm font-medium text-white md:absolute md:inset-x-0 md:bottom-12 md:mt-0">
              <span className="inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faShieldCheck} className="h-4 w-4 text-lime-burst" />
                Background-checked pros
              </span>
              <span className="inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faSparkles} className="h-4 w-4 text-lime-burst" />
                Ready-to-post social content
              </span>
              <span className="inline-flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-lime-burst" />
                48-hour delivery guarantee
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
